import { buildRhythmSummary } from '@/lib/exercise/rhythmSummary';
import type { RhythmScore } from '@/lib/exercise/rhythmEngine';

/**
 * Histórico de tentativas e de exercícios, salvo no progresso local.
 *
 * Cada registro guarda a data, a aula, o tipo (tentativa da aula ou exercício
 * individual), o resultado (incompleta / concluída / aula dominada) e a contagem
 * por item quando aplicável (nota, ataque, duração, pausa e ligadura). Fica numa
 * chave própria do armazenamento, separada do progresso de domínio, para que
 * limitar o número de registros NUNCA apague aulas já desbloqueadas.
 */

export interface ItemTally {
  correct: number;
  wrong: number;
}

export interface AttemptItems {
  note?: ItemTally;
  attack?: ItemTally;
  duration?: ItemTally;
  rest?: ItemTally;
  tie?: ItemTally;
}

export type AttemptResult = 'incomplete' | 'completed' | 'clean' | 'mastered';

export interface AttemptRecord {
  id: string;
  /** epoch em milissegundos */
  at: number;
  lessonId: number;
  scope: 'lesson' | 'exercise';
  exerciseIndex?: number;
  timeSignature?: string;
  result: AttemptResult;
  accuracy?: number;
  items?: AttemptItems;
}

/** Limite de registros guardados (os mais antigos saem primeiro). */
export const MAX_ATTEMPTS = 300;

export function makeAttemptId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Traduz um placar de ritmo nos cinco itens, para guardar no histórico. */
export function itemsFromRhythmScore(
  score: RhythmScore,
  hasRests: boolean,
  hasTies: boolean,
  measuresDuration: boolean,
): AttemptItems {
  const summary = buildRhythmSummary(score, { hasRests, hasTies, measuresDuration });
  const items: AttemptItems = {};
  summary.forEach((item) => {
    items[item.key] = { correct: item.correct, wrong: item.wrong };
  });
  return items;
}

/** Mantém no máximo MAX_ATTEMPTS registros (lista já vem do mais recente). */
export function trimAttempts(list: AttemptRecord[]): AttemptRecord[] {
  if (list.length <= MAX_ATTEMPTS) return list;
  return list.slice(0, MAX_ATTEMPTS);
}

export const ATTEMPT_RESULT_LABEL: Record<AttemptResult, string> = {
  incomplete: 'Tentativa incompleta',
  completed: 'Tentativa concluída',
  clean: 'Tentativa sem erros',
  mastered: 'Aula dominada',
};

export const ATTEMPT_ITEM_LABEL: Record<keyof AttemptItems, string> = {
  note: 'Nota',
  attack: 'Ataque',
  duration: 'Duração',
  rest: 'Pausa',
  tie: 'Ligadura',
};